import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { HELP_CENTER_GUIDE_METAS } from "../../data/helpCenterGuides";
import { NEUTRAL } from "../../styles/brandTokens";

const accent = "#705d4a";
const cardBorder = "1px solid #e8e4df";

const HelpCenterGuidesHub: React.FC = () => {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return HELP_CENTER_GUIDE_METAS;
    return HELP_CENTER_GUIDE_METAS.filter(
      (g) =>
        g.title.toLowerCase().includes(s) ||
        g.description.toLowerCase().includes(s) ||
        g.category.toLowerCase().includes(s)
    );
  }, [q]);

  const featured = useMemo(() => {
    const base = HELP_CENTER_GUIDE_METAS.filter((g) => g.featured);
    if (!q.trim()) return base;
    const id = new Set(filtered.map((g) => g.slug));
    return base.filter((g) => id.has(g.slug));
  }, [q, filtered]);

  const byCategory = useMemo(() => {
    const map = new Map<string, typeof HELP_CENTER_GUIDE_METAS>();
    for (const g of filtered) {
      const list = map.get(g.category) || [];
      list.push(g);
      map.set(g.category, list);
    }
    return map;
  }, [filtered]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        background: NEUTRAL.page,
        pb: 6,
      }}
    >
      <Box
        sx={{
          maxWidth: 1040,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          pt: { xs: 3, md: 5 },
          pb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: { xs: 28, md: 36 },
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: "#0f172a",
            textAlign: "center",
            mb: 1.5,
          }}
        >
          Theo Help Center
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: { xs: 15, md: 16 },
            lineHeight: 1.65,
            color: "#64748b",
            textAlign: "center",
            maxWidth: 560,
            mx: "auto",
            mb: 3,
          }}
        >
          Guides for using Theo with Microsoft 365, documentation tools, search, and your PSA.
        </Typography>

        <TextField
          fullWidth
          placeholder="Search guides…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 520,
            mx: "auto",
            display: "block",
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              background: "#fff",
              fontFamily: "var(--theo-font-sans)",
            },
          }}
        />
      </Box>

      <Box sx={{ maxWidth: 1040, mx: "auto", px: { xs: 2, sm: 3 } }}>
        {featured.length > 0 && (
          <>
        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#64748b",
            mb: 2,
            mt: 1,
          }}
        >
          Featured guides
        </Typography>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mb: 5 }}
          useFlexGap
        >
          {featured.map((g) => (
            <Card
              key={g.slug}
              elevation={0}
              sx={{
                flex: 1,
                minWidth: 0,
                border: cardBorder,
                borderRadius: 3,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <CardActionArea
                component={RouterLink}
                to={`/help-center/guides/${g.slug}`}
                sx={{
                  display: "block",
                  height: "100%",
                  p: 0,
                }}
              >
                <Box
                  sx={{
                    height: 120,
                    background: `linear-gradient(145deg, rgba(112,93,74,0.12) 0%, rgba(248,250,252,1) 55%, rgba(112,93,74,0.06) 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      background: "#fff",
                      boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: accent,
                      fontFamily: "var(--theo-font-sans)",
                      fontWeight: 800,
                      fontSize: 18,
                    }}
                  >
                    {g.title.charAt(0)}
                  </Box>
                </Box>
                <Box sx={{ p: 2.25 }}>
                  <Typography
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      fontWeight: 700,
                      fontSize: 16,
                      lineHeight: 1.35,
                      color: "#0f172a",
                      mb: 0.75,
                    }}
                  >
                    {g.title}
                  </Typography>
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 14, color: "#64748b", lineHeight: 1.55 }}>
                    {g.description}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1.5, color: accent }}>
                    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 13 }}>
                      Read guide
                    </Typography>
                    <ArrowForwardIosRoundedIcon sx={{ fontSize: 12 }} />
                  </Stack>
                </Box>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
          </>
        )}

        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#64748b",
            mb: 2,
          }}
        >
          Categories
        </Typography>

        <Stack spacing={3}>
          {Array.from(byCategory.entries()).map(([category, guides]) => (
            <Box key={category}>
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#0f172a",
                  mb: 1.5,
                }}
              >
                {category}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} flexWrap="wrap" useFlexGap spacing={2}>
                {guides.map((g) => (
                  <Card
                    key={g.slug}
                    elevation={0}
                    sx={{
                      flex: { sm: "1 1 calc(50% - 8px)" },
                      minWidth: { sm: 260 },
                      border: cardBorder,
                      borderRadius: 2,
                      background: "#fff",
                    }}
                  >
                    <CardActionArea
                      component={RouterLink}
                      to={`/help-center/guides/${g.slug}`}
                      sx={{ p: 2, alignItems: "flex-start", textAlign: "left" }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: 700,
                          fontSize: 15,
                          color: "#0f172a",
                          mb: 0.5,
                        }}
                      >
                        {g.title}
                      </Typography>
                      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
                        {g.description}
                      </Typography>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        {filtered.length === 0 && (
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#64748b", py: 4, textAlign: "center" }}>
            No guides match your search.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default HelpCenterGuidesHub;
